<template>
	<header :class="classes">
		<div
			v-if="isTablet ? !showPersonification : true"
			class="header__logo"
		>
			<helio-link
				v-if="homeUrl || logo"
				:link="homeUrl"
			>
				<img
					:src="logo"
					alt="logo"
				>
			</helio-link>
		</div>

		<div :class="`header__slot ${theme}-theme`">
			<slot name="default" />
		</div>

		<div class="header__items">
			<!-- START OF MODULES -->
			<div
				v-if="hasModules"
				:style="{ position: 'relative' }"
			>
				<div
					v-if="showModulesButton"
					:tabindex="tabIndex"
					data-anchor="modules"
					:class="[
						'header__modules header__item',
						{
							'header__modules--opened': showModules,
						}
					]"
					role="button"
					@click.stop="toggleModules"
					@keyup.space="toggleModules"
					@keyup.enter="toggleModules"
				>
					<helio-icon
						token="modules-grid"
						weight="bold"
					/>

					<div
						v-if="modulesText"
						class="header__modules-text"
					>
						{{ modulesText }}
					</div>
				</div>

				<helio-modules
					:active="showModules"
					:view="isMobile || isTablet ? 'panel' : 'popup'"
					:modules="modules"
					:modules-text="modulesText"
					:links="extraLinks"
					@close="showModules = false"
					@modules-panel-active="showModules = true"
					@modules-panel-inactive="showModules = false"
				/>
			</div>
			<!-- END OF MODULES -->

			<!-- START OF PERSONIFICATION -->
			<helio-personification
				v-if="showPersonification"
				:active="showPersonification"
				@close="showPersonification = false"
				@userSelected="setNewUser"
				@apiError="$emit('apiError')"
			/>
			<!-- END OF PERSONIFICATION -->

			<!-- START OF AVATAR -->
			<div :style="{ position: 'relative' }">
				<div v-if="hasUserInfoSlot">
					<slot name="user-info" />
				</div>

				<div
					v-else-if="user && !showPersonification"
					:tabindex="tabIndex"
					:class="[
						'header__avatar header__item',
						{
							'header__avatar--opened': showUserCard,
						}
					]"
					role="button"
					@click.stop="toggleUserPanel"
					@keyup.space="toggleUserPanel"
					@keyup.enter="toggleUserPanel"
				>
					<helio-avatar
						:user="avatarUser"
						:badge="user.personification && { position: 'top-right' }"
						size="small"
					/>

					<div
						v-if="showUserInformations"
						class="header__avatar-info"
					>
						<span
							v-if="user && user.name && user.family_name"
							class="header__avatar-name"
						>
							{{ user.name }} {{ user.family_name }}
						</span>

						<span
							v-if="user.personification"
							class="header__avatar-status"
						>
							{{ $t('header.avatar.personified') }}
						</span>
					</div>

					<helio-icon
						class="header__avatar-arrow"
						:token="showUserCard ? 'angle-up' : 'angle-down'"
					/>
				</div>

				<div v-if="hasUserContentSlot">
					<slot name="user-content" />
				</div>

				<helio-user-card
					v-else-if="user"
					:active="showUserCard && !showPersonification"
					:user="user"
					:view="showUserCard && !showPersonification && !isTablet ? 'popup' : 'panel'"
					:navigation-links="navigationLinks"
					:account-settings-link="accountSettingsLink"
					@usercard-close="showUserCard = false"
					@showPersonification="showPersonification = true"
					@endPersonification="$emit('endPersonification')"
					@logout="$emit('logoutClick')"
					@close-usercard="showUserCard = false"
				/>
			</div>
			<!-- END OF AVATAR -->

			<!-- START OF TRANSLATION -->
			<div
				v-if="basicTranslation"
				class="header__locale header__item"
			>
				<helio-link :link="translationLocaleUrl">
					{{ translationLocale }}
				</helio-link>
			</div>

			<div
				v-else-if="isMobile ? translationLocale && !showPersonification : translationLocale"
				class="header__locale header__item"
				role="button"
				@click="$emit('translationClick')"
				@keyup.space="$emit('translationClick')"
			>
				<span v-if="!translationLocaleUrl">
					{{ translationLocale }}
				</span>

				<router-link
					v-if="translationLocaleUrl"
					:to="translationLocaleUrl"
				>
					{{ translationLocale }}
				</router-link>
			</div>
			<!-- END OF TRANSLATION -->
		</div>
	</header>
</template>

<script>
import Avatar from '../avatar/Avatar.vue';
import Icon from '../icon/Icon.vue';
import Personification from './personification/Personification.vue';
import UserCard from './user-card/UserCard.vue';
import Modules from './modules/Modules.vue';

export default {
	name: 'HeaderComponent',

	components: {
		'helio-avatar': Avatar,
		'helio-icon': Icon,
		'helio-personification': Personification,
		'helio-user-card': UserCard,
		'helio-modules': Modules,
	},

	props: {
		accountSettingsLink: {
			type: String,
			required: false,
			default: null,
			description: 'Adds a button pointing to account settings ',
		},

		basicTranslation: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Decides whether we use a simple link or the propretiary Centiva version of the translation link',
		},

		extraLinks: {
			type: Object,
			required: false,
			default: () => null,
			description: 'Adds additional links in the left-hand side of the modules panel',
		},

		homeUrl: {
			type: String,
			required: false,
			default: null,
			description: 'Adds a link pointing to the home page',
		},

		logo: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the logo',
		},

		logout: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether a logout button will be displayed',
		},

		logoutMobileText: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text on the mobile logout button',
		},

		modules: {
			type: Array,
			required: false,
			default: () => ([]),
			description: 'Sets whether modules will be displayed and supplies information about the modules to the Header',
		},

		modulesButton: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the module button will appear',
		},

		modulesText: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text in the modules button',
		},

		navigationLinks: {
			type: Array,
			required: false,
			default: () => [],
			description: 'Sets the links in the user\'s navigation panel',
		},

		showUserInformations: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether we are displaying the user name and personification status or not',
		},

		theme: {
			type: String,
			required: false,
			default: 'dark',
			validator: value => [
				'dark',
				'light',
			].indexOf(value) !== -1,
		},

		translationLocale: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text for the translation language',
		},

		translationLocaleUrl: {
			type: [String, Object],
			required: false,
			default: null,
			description: 'Sets the route for the translationLocale',
		},

		user: {
			type: Object,
			required: false,
			default: null,
			description: 'Supplies information about the user to the Header component. The object passed can provide the user\'s name, family_name and email',
		},
	},

	slots: {
		default: {
			required: false,
			scoped: false,
			description: 'Supplies content to the Header component',
		},

		userContent: {
			required: false,
			scoped: false,
			description: 'Supplies custom user content to the Header',
		},

		userInfo: {
			required: false,
			scoped: false,
			description: 'Supplies custom user info to the Header',
		},
	},

	data() {
		return {
			panel: false,
			showModules: false,
			tabIndex: 0,
			showPersonification: false,
			showUserCard: false,
			isTablet: false,
			isMobile: false,
		};
	},

	computed: {
		hasModules() {
			return this.modules.length > 0;
		},

		classes() {
			return this.modules.length > 0 ? 'header' : 'header header--no-modules';
		},

		hasUserInfoSlot() {
			return !!this.$slots['user-info'] || !!this.$scopedSlots['user-info'];
		},

		hasUserContentSlot() {
			return !!this.$slots['user-content'] || !!this.$scopedSlots['user-content'];
		},

		showModulesButton() {
			return this.isMobile
				? this.hasModules && this.modulesButton && !this.showPersonification
				: this.hasModules && this.modulesButton;
		},

		avatarUser() {
			const avatarUser = this.cloneObject(this.user);

			if (avatarUser.name && avatarUser.family_name) {
				avatarUser.name = `${avatarUser.name} ${avatarUser.family_name}`;
			}

			return avatarUser;
		},
	},

	watch: {
		user() {
			this.showPersonification = false;
		},

		showModules() {
			if (this.showModules) {
				this.showUserCard = false;
				this.showPersonification = false;
			}
		},

		showUserCard() {
			if (this.showUserCard) {
				this.showModules = false;
				this.showPersonification = false;
			}
		},
	},

	mounted() {
		window.addEventListener('resize', this.resizeCheck);
		this.resizeCheck();
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.resizeCheck);
	},

	methods: {
		resizeCheck() {
			this.isMobile = window.innerWidth <= 420;
			this.isTablet = window.innerWidth <= 768;
		},

		toggleUserPanel() {
			this.showUserCard = !this.showUserCard;
			this.$emit('avatarClick');
		},

		setNewUser(newUser) {
			this.$emit('userSelected', newUser);
			this.showPersonification = false;
		},

		toggleModules() {
			if (this.showModules) {
				this.$emit('closeModules');
			} else {
				this.$emit('openModules');
			}

			this.showModules = !this.showModules;
		},

		closeModules() {
			this.showModules = false;
		},
	},
};
</script>

<style lang="scss" src="./header-component.scss" />
